import { useEffect, useRef, useState } from "react";
import { vadThreshold as vadThresholdSignal, silenceThreshold as silenceThresholdSignal } from "../../features/audio/state/signals.ts";
import logger, { getErrorForLog, getSafeErrorLog, safeSerializeForLog } from 'app/utils/logger.ts';

let activeWebSocket: WebSocket | null = null;

const AUDIO_SERVER_CONFIG = {
  vad: {
    sampleRate: 48000,    // 16kHz for Voice Activity Detection
    description: 'Voice Activity Detection Server'
  },
  openai: {
    sampleRate: 24000,    // 24kHz for OpenAI Realtime API
    description: 'OpenAI Realtime API Server'
  }
};


export const useWebsocketAudioModule = (
  {
    wsUrl = 'ws://localhost:8079',
    serverType = 'openai',
    mode = 'wav',
    provider = 'sklls',
    vadThreshold,
    silenceThreshold,
    lang,
    smartTurnEnabled,
    readyToListen,
    onVADStatus,
    onSpeechStart,
    onSpeechEnd,
    onTranscription,
    onTranscriptions,
    onWavClip,
    onWavAndTranscriptionClip,
    onTranscriptionFailed
  }:
    {
      wsUrl: string,
      serverType: 'vad' | 'openai',
      mode: 'wav' | 'both' | 'dual' | 'multilingual',
      provider?: string,
      vadThreshold: number,
      silenceThreshold: number,
      lang?: string,
      readyToListen: boolean,
      smartTurnEnabled?: boolean,
      onVADStatus?: (isSpeech, probability, ts) => void,
      onSpeechStart: (ts) => void,
      onSpeechEnd: (durationMs: number, ts) => void,
      onTranscription?: (text: string, language: string, ts) => void,
      onTranscriptions?: (transcriptions: Array<{ language: string, text: string }>, ts) => void,
      onWavClip?: (data: string, ts) => void,
      onWavAndTranscriptionClip?: (data: string, text: string, ts) => void,
      onTranscriptionFailed?: (language: string, ts: number) => void
    }) => {
  // Audio references
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  // WebSocket reference
  const [ws, setWs] = useState<WebSocket | null>(null)

  // Recording state  
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  const readyToListenRef = useRef(readyToListen);
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const isRecordingRef = useRef<boolean>(false)

  const manuallyStoppedRef = useRef(false)

  useEffect(() => {
    mediaStreamRef.current = mediaStream;
  }, [mediaStream]);
  useEffect(() => {
    wsRef.current = ws;
  }, [ws]);
  useEffect(() => {
    audioContextRef.current = audioContext;
  }, [audioContext]);
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  useEffect(() => {
    readyToListenRef.current = readyToListen;
  }, [readyToListen]);


  // Audio and chunking constants
  const TARGET_SAMPLE_RATE = AUDIO_SERVER_CONFIG[serverType].sampleRate;
  const FRAME_SIZE = 512;          // 512 samples
  const BYTES_PER_SAMPLE = 8;      // 16-bit = 2 bytes
  const CHUNK_SIZE = FRAME_SIZE * BYTES_PER_SAMPLE; // 1024 bytes

  // ------------------------------------------------------------------
  // Reconnection logic
  // ------------------------------------------------------------------
  const RECONNECT_BASE_DELAY = 2000;          // first retry after 2 s
  const MAX_RECONNECT_DELAY   = 30000;        // cap back-off at 30 s
  const reconnectAttemptsRef  = useRef(0);    // #attempts since last success
  const reconnectTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Schedule (or reschedule) a reconnect attempt with back-off.
   * @param resetAttempts – when true, start back-off at the base delay
   */
  const scheduleReconnect = (resetAttempts = false) => {
    if (resetAttempts) {
      reconnectAttemptsRef.current = 0;
    }
    const attempt = reconnectAttemptsRef.current;
    const delay =
      attempt === 0
        ? RECONNECT_BASE_DELAY
        : Math.min(RECONNECT_BASE_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);

    // Clear any previous timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }

    reconnectTimerRef.current = setTimeout(() => {
      reconnectAttemptsRef.current += 1;
      logger.warn(
        `[WebsocketAudioModule] Attempting reconnect (attempt ${reconnectAttemptsRef.current}) after ${delay} ms`
      );
      _connectWebSocket();
    }, delay);
  };


  /**
   * Connects WebSocket and sets up event handlers.
   * Called internally during start().
   */
  const _connectWebSocket = (): WebSocket => {
    // Build URL with all parameters
    const params = new URLSearchParams({
      mode: mode
    });

    // Add provider parameter if defined
    if (provider !== undefined) {
      params.append('provider', provider);
    }

    // Add optional parameters if they are defined
    if (vadThreshold !== undefined) {
      params.append('vad_threshold', vadThreshold.toString());
    }
    if (silenceThreshold !== undefined) {
      params.append('silence_threshold', silenceThreshold.toString());
    }
    if (lang !== undefined) {
      params.append('lang', lang);
    }
    if (smartTurnEnabled !== undefined) {
      params.append('smart_turn', smartTurnEnabled ? 'true' : 'false');
    }
    // Add sample rate parameter (falling back to the server type's sample rate)
    params.append('sr', TARGET_SAMPLE_RATE.toString());

    // If there's already an active WebSocket, close it (any state except CLOSED)
    if (activeWebSocket && activeWebSocket.readyState !== WebSocket.CLOSED) {
      logger.log('[WebsocketAudioModule] Closing existing WebSocket instance to ensure only one active instance.');
      activeWebSocket.close();
    }

    const wsUrlWithParams = `${wsUrl}?${params.toString()}`;
    const newWs = new WebSocket(wsUrlWithParams);

    // Assign to global ref
    activeWebSocket = newWs;
    wsRef.current = newWs;
    setWs(newWs);

    manuallyStoppedRef.current = false;

    newWs.onopen = () => {
      logger.log('[WebsocketAudioModule] WebSocket connected:', wsUrlWithParams);
      // Successful connection → reset back-off
      reconnectAttemptsRef.current = 0;
    };

    newWs.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        // If not JSON, ignore or handle raw data
        logger.warn('[WebsocketAudioModule] Received non-JSON message:', event.data);
        return;
      }

      const { timestamp, event: ev } = msg || {};

      switch (ev) {
        case 'vadStatus':
          // { event: "vadStatus", isSpeech: boolean, probability: number, timestamp: number }
          if (onVADStatus) {
            onVADStatus(msg.isSpeech, msg.probability, timestamp);
          }
          break;
        case 'speechStart':
          // { event: "speechStart", timestamp: number }
          onSpeechStart(timestamp);
          break;
        case 'speechEnd':
          // { event: "speechEnd", durationMs: number, timestamp: number }
          onSpeechEnd(msg.durationMs, timestamp);
          break;
        case 'transcription':
          // { event: "transcription", text: string, language: string, timestamp: number }
          if (onTranscription) {
            onTranscription(msg.text, msg.language, timestamp);
          }
          break;
        case 'transcriptions':
          // { event: "transcriptions", transcriptions: [{language: string, text: string}], timestamp: number }
          if (onTranscriptions) {
            onTranscriptions(msg.transcriptions, timestamp);
          }
          break;
        case 'wavClip':
          // { event: "wavClip", data: "base64-encoded-wav-data", timestamp: number }
          if (onWavClip) {
            onWavClip(msg.data, timestamp);
          }
          break;
        case 'wavAndTranscription':
          if (onWavAndTranscriptionClip) {
            onWavAndTranscriptionClip(msg.data, msg.text, timestamp);
          }
          break;
        case 'transcriptionFailed':
          // { event: "transcriptionFailed", language: string, timestamp: number }
          logger.warn(`[WebsocketAudioModule] Transcription failed for language: ${msg.language}`);
          if (onTranscriptionFailed) {
            onTranscriptionFailed(msg.language, timestamp);
          }
          break;
        case 'reconnect':
          logger.warn('[WebsocketAudioModule] Received reconnect event, scheduling WebSocket reconnect…');
          if (wsRef.current) {
            wsRef.current.close();
          }
          // Wait 2 s (base delay) before first retry, then exponential back-off
          scheduleReconnect(true);
          break;
        default:
          logger.log('[WebsocketAudioModule] Unrecognized event', {
            message: safeSerializeForLog(msg)
          });
      }
    };

    newWs.onclose = () => {
      logger.log('[WebsocketAudioModule] WebSocket closed.');
      if (activeWebSocket === newWs) {
        activeWebSocket = null;
      }
      // Attempt automatic reconnection
      if (!manuallyStoppedRef.current) {
        // Unexpected drop – try to reconnect
        scheduleReconnect();
      } else {
        // Reset flag; next "Play" will enable reconnects again
        manuallyStoppedRef.current = false;
      }
    };

    newWs.onerror = (err) => {
      logger.error('[WebsocketAudioModule] WebSocket error', {
        eventType: err.type,
        target: err.target != null ? String(err.target) : undefined
      });
    };

    return newWs;
  }

  /**
   * Downsamples the float32 buffer from input sample rate (e.g. 44100) to target sample rate (16kHz for VAD, 24kHz for OpenAI).
   */
  const _downsampleBuffer = (buffer: Float32Array, inputSampleRate: number, outputSampleRate: number): Float32Array => {
    if (outputSampleRate === inputSampleRate) {
      return buffer;
    }
    const ratio = inputSampleRate / outputSampleRate;
    const newLength = Math.round(buffer.length / ratio);
    const downsampled = new Float32Array(newLength);

    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < newLength) {
      downsampled[offsetResult++] = buffer[Math.round(offsetBuffer)];
      offsetBuffer += ratio;
    }
    return downsampled;
  }

  /**
   * Converts a Float32Array in [-1,1] to 16-bit signed PCM (Int16Array).
   */
  const _floatTo16BitPCM = (floatSamples: Float32Array) => {
    const output = new Int16Array(floatSamples.length);
    for (let i = 0; i < floatSamples.length; i++) {
      let s = floatSamples[i];
      // clamp
      s = Math.max(-1, Math.min(1, s));
      // scale to 16-bit
      s = s < 0 ? s * 32768 : s * 32767;
      output[i] = s;
    }
    return output.buffer;
  }

  /**
   * Starts the audio capture from the microphone and WebSocket streaming.
   */
  const start = async () => {
    if (isRecording) {
      logger.warn('[WebsocketAudioModule] Already recording, ignoring start().');
      return;
    }
    manuallyStoppedRef.current = false;

    let inputNode = null;
    let processorNode = null;
    let gainMode = null;
    let pcmBuffer = new Uint8Array(0);
    try {
      const newMediastream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = newMediastream;
      setMediaStream(newMediastream);
      // Create an AudioContext at 44100 Hz (typical browser default)
      const newAudioContext = new AudioContext();
      audioContextRef.current = newAudioContext;
      setAudioContext(newAudioContext);
      logger.log('[WebsocketAudioModule] Microphone access granted, sample rate:', newAudioContext.sampleRate);

      // Connect WebSocket
      const newWs = _connectWebSocket();

      // Create audio nodes
      inputNode = newAudioContext.createMediaStreamSource(newMediastream);
      // ScriptProcessor is deprecated in some contexts, but we'll mimic the example
      processorNode = newAudioContext.createScriptProcessor(2048, 1, 1);
      gainMode = newAudioContext.createGain();
      gainMode.gain.value = 0;

      // Handle audio data in the script processor callback
      processorNode.onaudioprocess = (audioEvent) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !readyToListenRef.current) {
          // WebSocket not ready
          return;
        }
        // Single-channel audio
        const inputBuffer = audioEvent.inputBuffer.getChannelData(0);

        // Downsample to target sample rate (16kHz for VAD, 24kHz for OpenAI)
        const downsampled = _downsampleBuffer(
          inputBuffer,
          newAudioContext.sampleRate,
          TARGET_SAMPLE_RATE
        );

        // Convert float samples to 16-bit PCM
        const pcm16 = _floatTo16BitPCM(downsampled);
        const newData = new Uint8Array(pcm16);

        // Append to the existing buffer
        const combined = new Uint8Array(pcmBuffer.length + newData.length);
        combined.set(pcmBuffer);
        combined.set(newData, pcmBuffer.length);
        pcmBuffer = combined;

        // Send complete 1024-byte chunks
        while (pcmBuffer.length >= CHUNK_SIZE) {
          const chunk = pcmBuffer.slice(0, CHUNK_SIZE);
          pcmBuffer = pcmBuffer.slice(CHUNK_SIZE);

          // Send raw audio chunk
          if (readyToListenRef.current) {
            wsRef.current.send(chunk.buffer);
          }
        }
      };

      // Connect audio graph
      inputNode.connect(processorNode);
      processorNode.connect(gainMode);
      gainMode.connect(newAudioContext.destination);

      isRecordingRef.current = true;
      setIsRecording(true);
      logger.log('[WebsocketAudioModule] Recording started.');
    } catch (err) {
      logger.error(getErrorForLog(err, '[WebsocketAudioModule] start() error'), { context: '[WebsocketAudioModule] start() error', error: getSafeErrorLog(err) });
      // Suppress reconnect attempts — this is a startup failure, not a drop
      manuallyStoppedRef.current = true;
      // Clean up any partially created resources
      _releaseResources();
    }
  }

  /**
   * Releases all audio/WebSocket resources based on their presence in refs.
   * Safe to call at any point during the lifecycle — startup, recording, or after stop.
   */
  const _releaseResources = () => {
    const audioContext = audioContextRef.current;
    const mediaStream = mediaStreamRef.current;
    const ws = wsRef.current;

    // Reset recording state
    isRecordingRef.current = false;
    setIsRecording(false);

    // Close AudioContext
    if (audioContext) {
      audioContextRef.current = null;
      setAudioContext(null);

      if (audioContext.state !== 'closed') {
        logger.log('[WebsocketAudioModule] Closing AudioContext.');
        audioContext.close().catch(err => {
          logger.warn(getErrorForLog(err, '[WebsocketAudioModule] AudioContext close error'), { context: '[WebsocketAudioModule] AudioContext close error', error: getSafeErrorLog(err) });
        });
      }
    }

    // Stop media tracks
    if (mediaStream) {
      logger.log('[WebsocketAudioModule] Stopping media tracks.');
      mediaStreamRef.current = null;
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }

    // Close WebSocket in any non-closed state (CONNECTING, OPEN, or CLOSING)
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      ws.close();
    }
    wsRef.current = null;
    setWs(null);

    // Cancel any scheduled reconnect
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    logger.log('[WebsocketAudioModule] Resources released.');
  }

  /**
   * Stops audio capture and closes resources (WebSocket, AudioContext).
   */
  const stop = () => {
    const hasResources = isRecordingRef.current ||
      mediaStreamRef.current || audioContextRef.current || wsRef.current;

    if (!hasResources) {
      logger.log('[WebsocketAudioModule] Nothing to stop.');
      return;
    }

    manuallyStoppedRef.current = true;
    _releaseResources();
    logger.log('[WebsocketAudioModule] Recording stopped.');
  }

  return { start, stop, isRecording, wsRef }

}
